import { Link } from 'react-router-dom'

import { fetchFips, fetchOrganizations, fetchStats } from '@/api'
import { ConvergenceArt } from '@/components/art'
import { FipCard } from '@/components/cards'
import {
    ErrorState,
    Hero,
    Loading,
    PageShell,
    PrincipleHint,
    Section,
    StatCard,
    Term,
    Tooltip,
} from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import { FIP_WIZARD_URL } from '@/links'
import { usePrinciple } from '@/principles'
import type { PrincipleCoverage, PrincipleGroup, Stats } from '@/types'

const groups: PrincipleGroup[] = ['F', 'A', 'I', 'R']
const RECENT_PROFILES = 3
const TOP_RESOURCES = 6
const TOP_VERIFIERS = 5

/** Quarter-sized steps, so that tiles stay legible and the legend stays short. */
function shareStep(share: number): number {
    return share === 0 ? 0 : Math.ceil(share * 4)
}

function CoverageTile({ item }: { item: PrincipleCoverage }) {
    const { t } = useI18n()
    const principle = usePrinciple(item.principle)
    const extra = t('overview.ofProfiles')
        .replace('{count}', String(item.fips_covered))
        .replace('{total}', String(item.fips_total))
    const step = shareStep(item.fips_total ? item.fips_covered / item.fips_total : 0)
    return (
        <Tooltip
            content={principle ? <PrincipleHint principle={principle} extra={extra} /> : extra}
        >
            <Link className={`tile tile--${step}`} to={`/resources?principles=${item.principle}`}>
                <span className="tile__code">{item.principle}</span>
                <span className="tile__value">
                    {item.fips_covered}/{item.fips_total}
                </span>
            </Link>
        </Tooltip>
    )
}

function CoverageMap({ coverage }: { coverage: PrincipleCoverage[] }) {
    const { t, groupName } = useI18n()
    return (
        <>
            <div className="coverage">
                {groups.map((group) => (
                    <div key={group} className="coverage__group">
                        <div className="group__head">
                            <span className={`group__badge group__badge--${group}`}>{group}</span>
                            <h3 style={{ margin: 0 }}>{groupName(group)}</h3>
                        </div>
                        <p className="coverage__text">{t(`glossary.group.${group}`)}</p>
                        <div className="tiles">
                            {coverage
                                .filter((item) => item.group === group)
                                .map((item) => (
                                    <CoverageTile key={item.principle} item={item} />
                                ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="legend">
                <strong>{t('overview.shareLegend')}:</strong>
                {['0', '≤ 25 %', '≤ 50 %', '≤ 75 %', '≤ 100 %'].map((label, step) => (
                    <span key={label}>
                        <span className={`tile tile--${step} legend__swatch`} />
                        {label}
                    </span>
                ))}
            </div>
        </>
    )
}

type Part = { key: string; label: string; count: number; className: string }

/** One whole split into labelled parts; the legend carries the exact numbers. */
function PartsBar({ title, parts }: { title: string; parts: Part[] }) {
    const total = parts.reduce((sum, part) => sum + part.count, 0)
    return (
        <div className="parts">
            <p className="label">{title}</p>
            <div className="parts__bar">
                {parts
                    .filter((part) => part.count > 0)
                    .map((part) => (
                        <span
                            key={part.key}
                            className={`parts__segment ${part.className}`}
                            style={{ flexGrow: part.count }}
                            title={`${part.label}: ${part.count}/${total}`}
                        />
                    ))}
            </div>
            <div className="legend legend--tight">
                {parts.map((part) => (
                    <span key={part.key}>
                        <span className={`matrix__marker ${part.className}`} />
                        {part.label} {part.count}
                    </span>
                ))}
            </div>
        </div>
    )
}

function RankRow({ to, label, value }: { to?: string; label: string; value: string | number }) {
    const content = (
        <>
            <span className="rank__label" title={label}>
                {label}
            </span>
            <span className="rank__value">{value}</span>
        </>
    )
    return to ? (
        <Link className="rank__row" to={to}>
            {content}
        </Link>
    ) : (
        <div className="rank__row">{content}</div>
    )
}

function StateCard({ data }: { data: Stats }) {
    const { t, localized, verificationName } = useI18n()
    const verifiedParts = (key: string, verified: number, total: number): Part[] => [
        {
            key: `${key}-verified`,
            label: verificationName('verified'),
            count: verified,
            className: 'matrix__marker--in-use',
        },
        {
            key: `${key}-other`,
            label: t('overview.notVerified'),
            count: total - verified,
            className: 'parts__segment--rest',
        },
    ]
    return (
        <div className="card">
            <h3>{t('overview.state')}</h3>
            <p className="section__hint">{t('overview.statusesHint')}</p>
            <PartsBar
                title={t('overview.statuses')}
                parts={data.by_status.map((item) => ({
                    key: item.key,
                    label: localized(item.label),
                    count: item.count,
                    className: `matrix__marker--${item.key}`,
                }))}
            />
            <PartsBar
                title={t('verification.stat')}
                parts={verifiedParts('resources', data.verified_resources, data.resources)}
            />
            <PartsBar
                title={t('overview.verifiedProfiles')}
                parts={verifiedParts('profiles', data.verified_fips, data.fips)}
            />
        </div>
    )
}

export function Overview() {
    const { t, localized } = useI18n()
    const stats = useAsync(fetchStats, [])
    const fips = useAsync(fetchFips, [])
    const organizations = useAsync(fetchOrganizations, [])

    const hero = (
        <Hero
            eyebrow={t('app.owner')}
            title={t('overview.heroTitle')}
            lead={t('overview.lead')}
            art={<ConvergenceArt />}
            actions={
                <>
                    <Link className="button button--light" to="/glossary">
                        {t('overview.newToFair')}
                    </Link>
                    <Link className="button button--ghost" to="/resources">
                        {t('overview.browseResources')}
                    </Link>
                    <Link className="button button--ghost" to="/matrix">
                        {t('nav.matrix')}
                    </Link>
                    <a
                        className="button button--ghost"
                        href={FIP_WIZARD_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={t('overview.authorHint')}
                    >
                        {t('overview.author')} ↗
                    </a>
                </>
            }
        />
    )

    if (stats.error) {
        return (
            <>
                {hero}
                <PageShell>
                    <ErrorState error={stats.error} onRetry={stats.reload} />
                </PageShell>
            </>
        )
    }
    if (!stats.data) {
        return (
            <>
                {hero}
                <PageShell>
                    <Loading />
                </PageShell>
            </>
        )
    }

    const data = stats.data
    const communities = new Map((organizations.data ?? []).map((item) => [item.id, item]))
    const recent = [...(fips.data ?? [])]
        .sort((a, b) => b.updated.localeCompare(a.updated))
        .slice(0, RECENT_PROFILES)

    return (
        <>
            {hero}
            <PageShell>
                <div className="grid grid--stats">
                    <StatCard value={data.fips} label={t('overview.stat.fips')} term="fip" />
                    <StatCard
                        value={data.resources}
                        label={t('overview.stat.resources')}
                        term="resource"
                    />
                    <StatCard
                        value={data.declarations}
                        label={t('overview.stat.declarations')}
                        term="declaration"
                    />
                    <StatCard
                        value={`${data.verified_resources}/${data.resources}`}
                        label={t('verification.stat')}
                        term="verification"
                    />
                </div>

                <Section
                    title={t('overview.coverage')}
                    hint={t('overview.coverageHint')}
                    term="principle"
                >
                    <CoverageMap coverage={data.coverage} />
                </Section>

                <div className="grid grid--three section">
                    <StateCard data={data} />

                    <div className="card">
                        <h3>
                            {t('overview.types')} <Term id="category" />
                        </h3>
                        <p className="section__hint">{t('overview.typesHint')}</p>
                        <div className="rank">
                            {data.by_type.map((item) => (
                                <RankRow
                                    key={item.key}
                                    to={`/resources?types=${item.key}`}
                                    label={localized(item.label)}
                                    value={item.count}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="stack">
                        <div className="card">
                            <h3>{t('overview.top')}</h3>
                            <p className="section__hint">{t('overview.topHint')}</p>
                            <div className="rank">
                                {data.top_resources.slice(0, TOP_RESOURCES).map((item) => (
                                    <RankRow
                                        key={item.resource}
                                        to={`/resources/${item.resource}`}
                                        label={item.name}
                                        value={`${item.usage_count}/${data.fips}`}
                                    />
                                ))}
                            </div>
                            <p style={{ margin: '0.8rem 0 0' }}>
                                <Link to="/matrix">{t('nav.matrix')} →</Link>
                            </p>
                        </div>
                        <div className="card">
                            <h3>
                                {t('verification.verifiers')} <Term id="verification" />
                            </h3>
                            <p className="section__hint">{t('verification.verifiersHint')}</p>
                            <div className="rank">
                                {data.by_verifier.slice(0, TOP_VERIFIERS).map((item) => (
                                    <RankRow
                                        key={item.key}
                                        label={localized(item.label)}
                                        value={item.count}
                                    />
                                ))}
                            </div>
                            {data.by_verifier.length > TOP_VERIFIERS ? (
                                <p className="item__meta" style={{ margin: '0.4rem 0 0' }}>
                                    +{data.by_verifier.length - TOP_VERIFIERS}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>

                <Section
                    title={t('overview.profiles')}
                    hint={t('fips.lead')}
                    action={
                        <Link to="/profiles">
                            {t('overview.allProfiles')} ({data.fips}) →
                        </Link>
                    }
                >
                    {fips.error ? (
                        <ErrorState error={fips.error} onRetry={fips.reload} />
                    ) : (
                        <div className="grid grid--cards">
                            {recent.map((fip) => (
                                <FipCard
                                    key={fip.id}
                                    fip={fip}
                                    community={communities.get(fip.community)}
                                    verifiers={communities}
                                />
                            ))}
                        </div>
                    )}
                </Section>
            </PageShell>
        </>
    )
}
